/**
 * agentPrefill — seed ADI's own authorised-recipient details into a new intake form.
 *
 * ADI is the registered migration agent on every matter, so the Department's
 * authorised-recipient questions have a known answer before the client logs in.
 * Those blocks are `client_hidden`, so nobody would ever fill them in by hand.
 * We write the answers at form-creation time instead, which means they are already
 * sitting in intake_field_data when staff go to transcribe the form into
 * ImmiAccount, and they appear on the staff PDF.
 *
 * Called from BOTH intake-form creation paths (CreateMatter, and IntakeFormV2's
 * lazy bootstrap). If a third ever appears, it must call this too.
 */
import { supabase } from './supabase';
import { agentPrefillFor } from '../schema/adi-agent';

/**
 * Insert the ADI-as-agent answers for `formId` against `intakeFormId`.
 *
 * Never overwrites an existing answer (ignoreDuplicates), so a staff correction
 * survives any later re-run or backfill. Best-effort: a failure here must not
 * fail matter creation, since the form is still perfectly usable without it and
 * the backfill script can repair it.
 */
export async function seedAgentPrefill(
  intakeFormId: string,
  formId: string,
  opts?: { variant?: string | null },
): Promise<void> {
  const prefill = agentPrefillFor(formId, opts);
  const rows = Object.entries(prefill).map(([field_key, field_value]) => ({
    intake_form_id: intakeFormId,
    field_key,
    field_value: field_value as never,
  }));
  if (rows.length === 0) return;

  const { error } = await supabase
    .from('intake_field_data')
    .upsert(rows, { onConflict: 'intake_form_id,field_key', ignoreDuplicates: true });

  if (error) {
    console.warn('Agent prefill failed (form still usable; run the backfill):', error);
  }
}
